import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { registerRoutingHook, removeRoutingHook } from '@adornis/router/client/routing-state-signal.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';

// NOTE: There should only ever be one instance of BHFormController. Otherwise there will be multiple dialogs on routing

/**
 * @deprecated because FormController is also deprecated
 */
export class BHFormController<
  T extends AdornisEntity | NarrowedEntity<AdornisEntity, BaseQLSelectionSet<AdornisEntity>>,
> extends FormController<T> {
  private routingHookId: number = -1;
  private beforeUnloadCallback?: (e: BeforeUnloadEvent) => void;

  protected translation = clientTranslator;

  private async maybeShowLeavePageDialog(...args) {
    const doc = this.document;
    if (!doc) return true;
    const isDirty = doc.isDirty(true);
    if (!isDirty) return true;
    const leavePage = await XDialog.confirm(this.translation.translate('form', 'confirm_leave_page'));
    if (leavePage) {
      console.warn('leaving page; reverted document', doc);
      doc.revert();
    }

    return leavePage;
  }

  private getBeforeUnloadCallback() {
    return (e: BeforeUnloadEvent) => {
      if (this.document?.isDirty(true)) e.preventDefault();
    };
  }

  override hostConnected() {
    super.hostConnected();
    this.registerRoutingGuard();
  }

  override hostDisconnected() {
    super.hostConnected();
    if (this.routingHookId !== -1) removeRoutingHook(this.routingHookId);
  }

  private registerRoutingGuard() {
    // intern routing
    if (this.routingHookId !== -1) removeRoutingHook(this.routingHookId);
    this.routingHookId = registerRoutingHook(this.maybeShowLeavePageDialog.bind(this));

    // browser routing
    if (this.beforeUnloadCallback) window.removeEventListener('beforeunload', this.beforeUnloadCallback);
    const beforeUnloadCallback = this.getBeforeUnloadCallback();
    window.addEventListener('beforeunload', beforeUnloadCallback);
    this.beforeUnloadCallback = beforeUnloadCallback;
  }
}
